import React, { useMemo } from 'react'
import {
    useCheckEditCampaignPermission,
    useCheckEditTemplatedCampaignPermission,
    usePermission,
} from 'hooks/permissions'
import { useNewTranslation, useWindowSize } from 'hooks'
import { Card, IndicatorColor } from './style'
import Divider from 'components/Shared/Divider'
import { CAMPAIGN_STATUS_ENUM, CampaignState } from 'state/campaigns'
import {
    CampaignManagement,
    CampaignStatusControl,
    TemplatedCampaignManagement,
} from 'components/Shared/Permission/enums'
import { useCheckSomePermissions } from 'hooks/permissions'
import { RectangleLinkButton } from 'particles/Button/RectangleButton'
import Text from 'particles/Text'
import Icon from 'particles/Icon'
import { getCampaignStatusTitle, getCampaignStatusColor } from 'components/utils'
import CampaignActions from 'components/Campaign/CampaignActions'
import { ButtonColor } from 'particles/Button'
import styled from 'particles/themes/styled'
import { getTeamTypeOptions } from 'components/Team/Teams/utils'
import { useSelector } from 'react-redux'
import { selectIsInternalUser } from 'state/profile'
import { TeamTypeTag } from 'components/TeamFilter'

interface CampaignCardProps {
    campaign: CampaignState
    onDuplicate: (uuid: string) => void
    campaignTypeLabel: string | null
    type: 'campaign' | 'template'
    onDelete: (campaign: CampaignState) => void
}

const CampaignCard: React.FC<CampaignCardProps> = ({ campaign, onDuplicate, campaignTypeLabel, type, onDelete }) => {
    const { uuid, status, title, country, brand, is_private, template_data, in_template_flow } = campaign
    const redirectToTemplate = in_template_flow && !!template_data?.uuid
    const [t] = useNewTranslation()
    const isAbleToEditCampaign = useCheckEditCampaignPermission(status, true, true)
    const isInternalUser = useSelector(selectIsInternalUser)
    const isAbleToEditTemplatedCampaign = useCheckEditTemplatedCampaignPermission(status, true)
    const teamTypeLabels = getTeamTypeOptions(t)
    const hasCreateNewCampaignPermission = useCheckSomePermissions([
        CampaignManagement.CreateAndEditBriefsForInternal,
        CampaignManagement.CreateAndEditBriefsForExternal,
        TemplatedCampaignManagement.CreateAndEditBriefsForInternal,
        TemplatedCampaignManagement.CreateAndEditBriefsForExternal,
    ])
    const windowSize = useWindowSize()
    const isMobileSize = windowSize <= 768

    const canViewPrivateCampaignBudget = usePermission(CampaignManagement.ShowPrivateCampaignBudget)
    const canPayCampaign = usePermission(CampaignStatusControl.PayCampaign)
    const canViewPrivatePaymentTab = canViewPrivateCampaignBudget && canPayCampaign
    const canViewPaymentTab =
        campaign.is_private && !isInternalUser
            ? canViewPrivatePaymentTab
            : useCheckSomePermissions([CampaignStatusControl.PayCampaign, CampaignStatusControl.LaunchCampaign])

    const { buttonText, buttonColor } = useMemo(() => {
        let buttonText = t('button.viewCampaign')
        let buttonColor: ButtonColor = 'neutral'

        if (status === CAMPAIGN_STATUS_ENUM.LIVE) {
            if (isAbleToEditCampaign || (in_template_flow && isAbleToEditTemplatedCampaign)) {
                buttonText = t('button.editCampaign')
                buttonColor = 'primary'
            }
        } else {
            if (isAbleToEditCampaign || (in_template_flow && isAbleToEditTemplatedCampaign)) {
                buttonColor = 'primary'
                buttonText = t('button.editCampaign')
                if (status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW) {
                    buttonText = t('button.reviewCampaign')
                } else if (status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT) {
                    buttonText = t('button.onCampaignCardPayCampaign')
                } else if (status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) {
                    buttonText = t('button.launchCampaign')
                }
            } else {
                if (
                    status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
                    status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
                    status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH
                ) {
                    buttonText = t('button.viewCampaign')
                }
            }
        }

        return { buttonText, buttonColor }
    }, [status, isAbleToEditCampaign, isAbleToEditTemplatedCampaign])

    const linkSuffix = useMemo(() => {
        let linkSuffix = ''
        if (
            [
                CAMPAIGN_STATUS_ENUM.FOR_EDIT,
                CAMPAIGN_STATUS_ENUM.FOR_REVIEW,
                CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT,
                CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH,
            ].includes(status)
        ) {
            linkSuffix = '&drawer=comments'
        } else if (status === CAMPAIGN_STATUS_ENUM.DRAFT) {
            linkSuffix = '&drawer=preview'
        }

        return isMobileSize ? '' : linkSuffix
    }, [status, isMobileSize])
    // TODO: update link after editor's payment page is ready
    let buttonLink = `/campaign/edit/registration?campaign=${uuid}`
    if (redirectToTemplate) {
        buttonLink = `/campaign/template/templateDetail?templateData=${template_data.uuid}&campaign=${uuid}`
        if (status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT || status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) {
            buttonLink = `/campaign/template/templateSummary?templateData=${template_data.uuid}&campaign=${uuid}`
        }
    } else {
        if (
            canViewPaymentTab &&
            (status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT || status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH)
        ) {
            buttonLink = `/campaign/edit/payment?campaign=${uuid}`
        }
    }
    return (
        <>
            <Card decBarColor={getCampaignStatusColor(status) as IndicatorColor} withTeamTypeTag={isInternalUser}>
                <div className="card-header">
                    <Text
                        scale="bodyS700"
                        color={getCampaignStatusColor(status, true)}
                        className="campaign-status"
                        case="uppercase"
                    >
                        {getCampaignStatusTitle(status, t)}
                    </Text>
                    {hasCreateNewCampaignPermission && (
                        <CampaignActions campaign={campaign} onDuplicate={onDuplicate} onDelete={onDelete} />
                    )}
                </div>

                <div className="card-main-info">
                    <BrandContainer>
                        <Text scale="bodyM500" color="secondary" className="brand-name">
                            {brand.name}
                        </Text>
                        {isInternalUser && !!brand?.team?.type && (
                            <TeamTypeTag label={teamTypeLabels[brand.team.type].title} />
                        )}
                    </BrandContainer>
                    <Divider />
                    <div className="Campaign-title">
                        {campaignTypeLabel && (
                            <div className="campaign-type">
                                <Icon name={type === 'template' ? 'magicWand' : 'Handshake'} />
                                <Text scale="caption500" color="secondary">
                                    {campaignTypeLabel}
                                </Text>
                            </div>
                        )}
                        <Text scale="H4" className="Campaign-title" color="primary">
                            {title}
                        </Text>
                    </div>

                    <div>
                        <RectangleLinkButton
                            form="outlined"
                            size="m"
                            color={buttonColor}
                            label={buttonText}
                            data-testid="test-campaign-card-button"
                            to={`${buttonLink}${linkSuffix}`}
                            className="width-full"
                        />
                    </div>
                </div>
                <Divider />
                <div className="card-footer">
                    <Text className="campaign-country" scale="bodyS400" color="secondary">
                        {country}
                    </Text>
                    <Text scale="bodyS400" color="secondary">
                        {is_private
                            ? t('card.allCampaigns.privateCampaignLabel')
                            : t('card.allCampaigns.publicCampaignLabel')}
                    </Text>
                </div>
            </Card>
        </>
    )
}

export default CampaignCard

const BrandContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[100]};
`
